import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { uploadAttachment } from './api';
import type { InterviewOwnerType } from '../interviews/api';

interface Props {
  interviewId: number;
  /** 客戶 id 或廠商 id(由 ownerType 決定;用於 query invalidation key) */
  ownerId: number;
  /** owner 類型,決定 upload endpoint 是 /interviews 還是 /supplier-interviews */
  ownerType: InterviewOwnerType;
  /**
   * REQ-20260421004:compact 模式只渲染一個「新增檔案」按鈕,
   * 省空間;進度以 button label 呈現,錯誤用 alert。
   * 預設 false(保留原本拖放區 UI,InterviewModal 編輯頁用)。
   */
  compact?: boolean;
}

type Progress = {
  name: string;
  pct: number;
  status: 'uploading' | 'done' | 'error';
  error?: string;
};

const MAX_BYTES = 10 * 1024 * 1024;
// 前端做提示用；實際白名單由後端最終決定。
const ALLOWED_EXT = /\.(pdf|docx?|xlsx?|pptx?|jpe?g|png|webp|csv|txt|zip)$/i;

export function AttachmentUploader({
  interviewId,
  ownerId,
  ownerType,
  compact = false,
}: Props): JSX.Element {
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [items, setItems] = useState<Progress[]>([]);
  const [dragOver, setDragOver] = useState(false);

  // compact 模式:錯誤直接 alert,不放進 items(避免長期堆積且無 UI 可看)
  const failItem = (name: string, error: string): void => {
    if (compact) {
      // eslint-disable-next-line no-alert
      window.alert(`${name}：${error}`);
      setItems((prev) => prev.filter((i) => i.name !== name));
      return;
    }
    setItems((prev) => update(prev, name, { status: 'error', error }));
  };

  const onFiles = async (files: FileList | File[]): Promise<void> => {
    const list = Array.from(files);
    if (list.length === 0) return;
    const nextItems: Progress[] = list.map((f) => ({
      name: f.name,
      pct: 0,
      status: 'uploading',
    }));
    setItems((prev) => [...prev, ...nextItems]);

    for (const file of list) {
      if (file.size === 0) {
        failItem(file.name, '檔案為空(0 byte),請確認已存檔有內容');
        continue;
      }
      if (file.size > MAX_BYTES) {
        failItem(file.name, '檔案超過 10MB');
        continue;
      }
      if (!ALLOWED_EXT.test(file.name)) {
        failItem(file.name, '不支援此檔案類型');
        continue;
      }
      try {
        await uploadAttachment(
          interviewId,
          file,
          (pct) => {
            setItems((prev) => update(prev, file.name, { pct }));
          },
          ownerType,
        );
        setItems((prev) => update(prev, file.name, { status: 'done', pct: 100 }));
      } catch (err) {
        failItem(file.name, err instanceof Error ? err.message : '上傳失敗');
      }
    }
    // 觸發 interview list 重抓，列表會出現新 attachments
    void qc.invalidateQueries({
      queryKey: ['interviews', ownerType, ownerId],
      exact: false,
    });
  };

  if (compact) {
    const active = items.find((i) => i.status === 'uploading');
    const label = active ? `上傳中 ${active.pct}%` : '＋ 新增檔案';
    return (
      <>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={active != null}
          className="text-sm border rounded px-2 py-0.5 hover:bg-slate-100 disabled:opacity-50"
          data-testid="btn-compact-attach"
        >
          {label}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          aria-label="選擇附件檔案"
          className="hidden"
          onChange={(e) => {
            if (e.target.files) void onFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </>
    );
  }

  return (
    <div>
      <div
        role="button"
        tabIndex={0}
        aria-label="附件上傳區"
        onClick={() => fileInputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click();
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          void onFiles(e.dataTransfer.files);
        }}
        className={`text-sm text-center px-3 py-2 border-2 border-dashed rounded cursor-pointer ${
          dragOver ? 'border-blue-500 bg-blue-50' : 'border-slate-300 bg-slate-50 hover:bg-slate-100'
        }`}
      >
        拖放檔案到此或點擊選檔（單檔最大 10MB；PDF / Office / 圖片 / zip）
      </div>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        aria-label="選擇附件檔案"
        className="hidden"
        onChange={(e) => {
          if (e.target.files) void onFiles(e.target.files);
          e.target.value = '';
        }}
      />
      {items.length > 0 ? (
        <ul className="mt-2 grid gap-1 text-xs">
          {items.map((it) => (
            <li key={it.name} className="flex items-center gap-2">
              <span className="truncate max-w-[16rem]">{it.name}</span>
              {it.status === 'uploading' ? (
                <span className="text-slate-500">{it.pct}%</span>
              ) : it.status === 'done' ? (
                <span className="text-green-600">✓</span>
              ) : (
                <span role="alert" className="text-red-600">
                  {it.error}
                </span>
              )}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function update(prev: Progress[], name: string, patch: Partial<Progress>): Progress[] {
  return prev.map((it) => (it.name === name ? { ...it, ...patch } : it));
}
